import React, { useCallback, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { Button, Modal, theme, VStack } from '@sg/garnish';

import { useScanToPayModalSafeAreaInsets } from '../../hooks';
import { type PaymentMethod } from '../../machine/scan-to-pay.machine.types';
import { ScanToPayAddPaymentMethodOption } from '../ScanToPayAddPaymentMethodOption';
import { ScanToPayPaymentMethodOption } from '../ScanToPayPaymentMethodOption';

export const ScanToPayPaymentSelectionModal = (
  props: ScanToPayPaymentSelectionModalProps,
) => {
  const {
    isVisible,
    isLoading,
    paymentMethodId,
    paymentMethods,
    onSaveChanges,
    onAddPaymentMethod,
    onRequestClose,
  } = props;

  const safeAreaStyle = useScanToPayModalSafeAreaInsets();

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] =
    useState(paymentMethodId);

  const canSave = selectedPaymentMethodId !== paymentMethodId;

  const handleSaveChanges = useCallback(() => {
    if (!canSave) return;

    onSaveChanges(selectedPaymentMethodId);
  }, [canSave, onSaveChanges, selectedPaymentMethodId]);

  return (
    <Modal
      size="medium"
      palette="neutral"
      fitHeight
      visible={isVisible}
      withSafeAreaView={false}
      onRequestClose={onRequestClose}
    >
      <View style={safeAreaStyle}>
        <Modal.CloseBtn
          palette="muted"
          onPress={onRequestClose}
          width={40}
          height={40}
        />

        <VStack gap={16} hasDivider={false}>
          {paymentMethods.map((paymentMethod) => (
            <ScanToPayPaymentMethodOption
              key={paymentMethod.id}
              isSelected={selectedPaymentMethodId === paymentMethod.id}
              paymentMethod={paymentMethod}
              onSelectPaymentMethod={setSelectedPaymentMethodId}
            />
          ))}

          <ScanToPayAddPaymentMethodOption
            onAddPaymentMethod={onAddPaymentMethod}
          />
        </VStack>

        <Modal.Footer withoutTopBorder style={styles.footer}>
          <Button
            size="large-wide"
            palette={canSave ? 'spinach' : 'muted'}
            style={styles.cta}
            isLoading={isLoading}
            disabled={!canSave}
            onPress={handleSaveChanges}
          >
            <FormattedMessage {...messages.cta} />
          </Button>
        </Modal.Footer>
      </View>
    </Modal>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  cta: {
    defaultMessage: 'Save changes',
    description: 'Scan to Pay | Points | CTA',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footer: {
    alignItems: 'center',
    paddingBottom: theme.spacing['4'],
  },
  cta: {
    marginTop: theme.spacing['6'],
    paddingHorizontal: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScanToPayPaymentSelectionModalProps = {
  isVisible: boolean;
  isLoading: boolean;
  paymentMethodId: string;
  paymentMethods: readonly PaymentMethod[];
  onSaveChanges: (paymentMethodId: string) => void;
  onAddPaymentMethod: () => void;
  onRequestClose: () => void;
};
